import { stumble } from '@ipfabric/frontend-shared-assets/utils/stumble/stumble';
import { PageLayout } from '@ipfabric/ui-components/PageLayout/PageLayout';
import { useLoadingState } from '@ipfabric/ui-components/hooks/useLoadingState';
import { withLoadingIndicator } from '@ipfabric/ui-components/utils/loading/withLoadingIndicator';
import type { RoleId } from '@ipfabric/utils';
import { Button } from 'components/Button/Button';
import { useLocale } from 'containers/App/LocaleContext';
import { LoaderAndText } from 'containers/LoaderAndText/LoaderAndText';
import { RolesTable } from 'pages/Settings/Administration/Roles/RolesTable';
import { type FC, memo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useConfirmDialog } from 'utils/hooks/useConfirmDialog';
import { useApi } from 'utils/request/useApi';
import { RoleForm, newRole } from './RoleForm';

const BareRolesPage: FC = () => {
  const { administrationRolesMessages: rolesMsg } = useLocale();
  const api = useApi();
  const [removing, setRemoving] = useLoadingState();
  const [editedRoleId, setEditedRoleId] = useState<
    RoleId | typeof newRole | null
  >(null);
  const [roleIdToRemove, setRoleIdToRemove] = useState<RoleId | null>(null);
  const [copiedRoleId, setCopiedRoleId] = useState<RoleId | null>(null);

  const deleteRoleConfirmDialog = useConfirmDialog({
    name: 'DELETE_ROLE_ROW_CONFIRM',
  });

  const title = (() => {
    if (editedRoleId === newRole) {
      return rolesMsg.titleAdd;
    }
    if (editedRoleId !== null) {
      return rolesMsg.titleEdit;
    }
    if (copiedRoleId !== null) {
      return rolesMsg.titleCopy;
    }
    return null;
  })();

  const renderContent = () => {
    if (removing === 'LOADING') {
      return <LoaderAndText />;
    }
    if (editedRoleId || copiedRoleId) {
      return (
        <PageLayout className='flex flex-col'>
          <h2>{title}</h2>
          <RoleForm
            copiedRoleId={copiedRoleId}
            editedRoleId={editedRoleId}
            onClose={() => {
              setEditedRoleId(null);
              setCopiedRoleId(null);
            }}
          />
        </PageLayout>
      );
    }
    return (
      <div className='flex flex-col flex-1 gap p'>
        <div>
          <Button
            variant='secondary'
            icon='fa-plus'
            onClick={() => {
              setEditedRoleId(newRole);
            }}
            type='button'
            dataTestId='roles-add-button'
          >
            {rolesMsg.addRoleButton}
          </Button>
        </div>
        <RolesTable
          onEditRole={(roleId) => {
            setEditedRoleId(roleId);
          }}
          onRemoveRole={(roleId) => {
            setRoleIdToRemove(roleId);
            deleteRoleConfirmDialog.toggleConfirmDialog();
          }}
          onCopyRole={(roleId) => {
            setCopiedRoleId(roleId);
          }}
        />
      </div>
    );
  };

  const removeRole = async (roleId: RoleId) => {
    if (roleId === null) {
      stumble('no attribute is set to be removed');
      return;
    }
    await withLoadingIndicator(setRemoving)(() =>
      api.request({
        url: `/roles/${encodeURIComponent(roleId)}`,
        method: 'DELETE',
      }),
    );
  };

  return (
    <>
      <Helmet title={title ?? 'Roles'} />
      {renderContent()}
      <deleteRoleConfirmDialog.DialogComponent
        msg={rolesMsg.deleteRoleConfirmDialog}
        onClickConfirm={() => {
          setRoleIdToRemove(null);
          if (roleIdToRemove === null) {
            stumble('No role is set to be removed');
            return;
          }
          removeRole(roleIdToRemove);
        }}
        onCloseClick={() => {
          setRoleIdToRemove(null);
        }}
        // because default button variants in confirm dialog are insane in current state :/
        btnConfirmAccent='negative'
        btnConfirmVariant='primary'
        btnCancelVariant='secondary'
      />
    </>
  );
};

export const RolesPage = memo(BareRolesPage);
